import { dollarsToCents } from '@fruitful/fruitils';
import type { AccountSummary, DepositAccount } from '@fruitful/types';
import { isRetirementRegistrationType } from '@fruitful/types';
import { useAtomValue } from 'jotai';
import { InputNumber } from 'primereact/inputnumber';
import React, { useMemo, useState } from 'react';
import { ScrollView } from 'react-native';
import {
    BodyLargeEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    CheckboxWithLabel,
    Spinner,
    YStack,
} from 'smoothie';

import useGetAccountsForMembership from '../../../banking/banking/hooks/useGetAccountsForMembership';
import { ApexAccountLineItem } from '../../../banking/components/ApexAccountLineItem';
import SelectAccountSheet from '../../../banking/components/SelectAccountSheet';
import TransferAccountLineItem from '../../../banking/components/TransferAccountLineItem';
import { GroveSimpleModal } from '../../../common/components/GroveSimpleModal';
import { captureException } from '../../../logger/Logger';
import { adminUserAtom } from '../../entityAtoms';
import type { MembershipDetails } from '../../entityTypes';
import useTestICTWithdrawal, {
    type TaxWithholding,
    type TestICTWithdrawalRequest,
} from '../hooks/investments/useTestICTWithdrawal';

// TODO jason
//   Move to shared package _after_ IM launch
enum RetirementDistributionCreateType {
    TYPE_UNSPECIFIED = 'TYPE_UNSPECIFIED',
    NORMAL = 'NORMAL',
    DISABILITY = 'DISABILITY',
    SOSEPP = 'SOSEPP',
    PREMATURE = 'PREMATURE',
    DEATH = 'DEATH',
    EXCESS_CONTRIBUTION_REMOVAL_BEFORE_TAX_DEADLINE = 'EXCESS_CONTRIBUTION_REMOVAL_BEFORE_TAX_DEADLINE',
    EXCESS_CONTRIBUTION_REMOVAL_AFTER_TAX_DEADLINE = 'EXCESS_CONTRIBUTION_REMOVAL_AFTER_TAX_DEADLINE',
    ROLLOVER_TO_QUALIFIED_PLAN = 'ROLLOVER_TO_QUALIFIED_PLAN',
    ROLLOVER_TO_IRA = 'ROLLOVER_TO_IRA',
    DISTRIBUTION_TRANSFER = 'DISTRIBUTION_TRANSFER',
    RECHARACTERIZATION_PRIOR_YEAR = 'RECHARACTERIZATION_PRIOR_YEAR',
    RECHARACTERIZATION_CURRENT_YEAR = 'RECHARACTERIZATION_CURRENT_YEAR',
    DISTRIBUTION_CONVERSION = 'DISTRIBUTION_CONVERSION',
    MANAGEMENT_FEE = 'MANAGEMENT_FEE',
    PLAN_LOAN_401K = 'PLAN_LOAN_401K',
    PREMATURE_SIMPLE_IRA_LESS_THAN_2_YEARS = 'PREMATURE_SIMPLE_IRA_LESS_THAN_2_YEARS',
    NORMAL_ROTH_IRA_GREATER_THAN_5_YEARS = 'NORMAL_ROTH_IRA_GREATER_THAN_5_YEARS',
    NET_INCOME_ATTRIBUTABLE = 'NET_INCOME_ATTRIBUTABLE',
    REVOCATION = 'REVOCATION',
    NON_REPORTABLE = 'NON_REPORTABLE',
}

export const ApexWithdrawalModal = ({
    account,
    membership,
    isOpen,
    onClose,
}: {
    account: AccountSummary;
    membership: MembershipDetails;
    isOpen: boolean;
    onClose: () => void;
}) => {
    const adminUser = useAtomValue(adminUserAtom);

    const isRetirementAccount = useMemo(
        () => isRetirementRegistrationType(account.type),
        [account.type],
    );

    const [selectedDepositAccount, setSelectedDepositAccount] =
        useState<DepositAccount | null>(null);
    const [showAccountSelector, setShowAccountSelector] = useState(false);
    const [amount, setAmount] = useState(0);
    const [distributionType, setDistributionType] = useState(
        RetirementDistributionCreateType.PREMATURE,
    );
    const [federalWithholdingMode, setFederalWithholdingMode] = useState<
        'percentage' | 'amount'
    >('percentage');
    const [federalWithholdingPct, setFederalWithholdingPct] = useState<
        number | null
    >(null);
    const [federalWithholdingAmt, setFederalWithholdingAmt] = useState<
        number | null
    >(null);
    const [stateWithholdingMode, setStateWithholdingMode] = useState<
        'percentage' | 'amount'
    >('percentage');
    const [stateWithholdingPct, setStateWithholdingPct] = useState<
        number | null
    >(null);
    const [stateWithholdingAmt, setStateWithholdingAmt] = useState<
        number | null
    >(null);
    const [stateWithholdingWaiver, setStateWithholdingWaiver] = useState(false);
    const [result, setResult] = useState('');
    const [hasSubmitted, setHasSubmitted] = useState(false);

    const { data: membershipAccounts, isPending } = useGetAccountsForMembership(
        {
            membershipId: membership.id,
        },
    );

    const { mutateAsync: submitWithdrawal, isPending: isSubmitting } =
        useTestICTWithdrawal({});

    const mappedAccounts: DepositAccount[] = useMemo(() => {
        return (
            membershipAccounts
                ?.filter((acct) => {
                    return !acct.isExternal;
                })
                .map((acct) => {
                    return {
                        ...acct,
                        accountNumberMasked: '',
                        accountType: '',
                        bankName: '',
                        accountNumberMaskedIsTokenized: false,
                    };
                }) ?? []
        );
    }, [membershipAccounts]);

    const withdrawlRequest: TestICTWithdrawalRequest = useMemo(() => {
        const base: TestICTWithdrawalRequest = {
            userId: account.userId,
            toSyncteraAccountId: selectedDepositAccount?.id ?? '',
            fromApexAccountId: account.id,
            amountCents: dollarsToCents(amount),
            memo: `Admin withdrawal initiated by ${adminUser.email}`,
        };
        if (isRetirementAccount) {
            let federalTaxWithholding: TaxWithholding | undefined;
            if (
                federalWithholdingMode === 'percentage' &&
                federalWithholdingPct != null
            ) {
                federalTaxWithholding = { percentage: federalWithholdingPct };
            } else if (
                federalWithholdingMode === 'amount' &&
                federalWithholdingAmt != null
            ) {
                federalTaxWithholding = {
                    amountCents: dollarsToCents(federalWithholdingAmt),
                };
            }

            let stateTaxWithholding: TaxWithholding | undefined;
            if (!stateWithholdingWaiver) {
                if (
                    stateWithholdingMode === 'percentage' &&
                    stateWithholdingPct != null
                ) {
                    stateTaxWithholding = { percentage: stateWithholdingPct };
                } else if (
                    stateWithholdingMode === 'amount' &&
                    stateWithholdingAmt != null
                ) {
                    stateTaxWithholding = {
                        amountCents: dollarsToCents(stateWithholdingAmt),
                    };
                }
            }

            base.retirementDistribution = {
                type: distributionType,
                federalTaxWithholding,
                stateTaxWithholding,
                stateWithholdingWaiver: stateWithholdingWaiver || undefined,
            };
        }
        return base;
    }, [
        selectedDepositAccount,
        distributionType,
        amount,
        isRetirementAccount,
        adminUser.email,
        federalWithholdingMode,
        federalWithholdingPct,
        federalWithholdingAmt,
        stateWithholdingMode,
        stateWithholdingPct,
        stateWithholdingAmt,
        stateWithholdingWaiver,
    ]);

    const canSubmit = useMemo(() => {
        return (
            account.userId != null &&
            amount > 0 &&
            selectedDepositAccount != null
        );
    }, [account, amount, selectedDepositAccount]);

    const isWaiting = useMemo(() => {
        return isPending || isSubmitting;
    }, [isPending, isSubmitting]);

    return (
        <GroveSimpleModal
            isOpen={isOpen}
            onClose={onClose}
            hasCloseButton
            renderInAppRoot={true}
        >
            <ScrollView>
                <YStack gap={32} padding={16} width={600} disabled={isWaiting}>
                    <BodyLargeEmphasized>Apex withdrawal</BodyLargeEmphasized>
                    {result !== '' && (
                        <YStack borderWidth={1} padding={16} gap={16}>
                            <BodyLargeEmphasized>
                                Withdrawal result
                            </BodyLargeEmphasized>
                            <BodyMediumRegular>{result}</BodyMediumRegular>
                        </YStack>
                    )}
                    <YStack gap={16}>
                        <BodySmallRegular>From</BodySmallRegular>
                        <ApexAccountLineItem account={account} />
                    </YStack>
                    <YStack gap={16}>
                        <BodySmallRegular>To</BodySmallRegular>
                        <TransferAccountLineItem
                            depositAccount={selectedDepositAccount}
                            placeholder="Select a bank account"
                            onPress={() => {
                                setShowAccountSelector(true);
                            }}
                            showActionIcon={true}
                        />
                    </YStack>
                    <YStack gap={16}>
                        <BodySmallRegular>Amount</BodySmallRegular>
                        <InputNumber
                            id="amount"
                            disabled={isWaiting}
                            value={amount}
                            onChange={(e) => {
                                setAmount(e.value ?? 0);
                            }}
                            mode="currency"
                            currency="USD"
                            min={0}
                        />
                    </YStack>
                    {isRetirementAccount && (
                        <YStack gap={16}>
                            <YStack>
                                <BodySmallRegular>
                                    Distribution reason
                                </BodySmallRegular>
                                {/* <SelectDropdown> doesn't render correctly here, */}
                                {/* copied this solution from <AccountCreateModal> */}
                                <select
                                    disabled={isWaiting}
                                    value={distributionType}
                                    onChange={(e) => {
                                        setDistributionType(
                                            e.target
                                                .value as RetirementDistributionCreateType,
                                        );
                                    }}
                                    style={{
                                        padding: 12,
                                        borderRadius: 8,
                                        borderWidth: 1,
                                        borderColor: '#ccc',
                                        fontSize: 16,
                                    }}
                                >
                                    {Object.values(
                                        RetirementDistributionCreateType,
                                    )
                                        .filter(
                                            (v) =>
                                                v !==
                                                RetirementDistributionCreateType.TYPE_UNSPECIFIED,
                                        )
                                        .map((v) => {
                                            return (
                                                <option
                                                    key={v.toString()}
                                                    value={v}
                                                >
                                                    {v.toString()}
                                                </option>
                                            );
                                        })}
                                </select>
                            </YStack>
                            <YStack gap={4}>
                                <BodySmallRegular>
                                    Federal tax withholding (optional)
                                </BodySmallRegular>
                                <select
                                    disabled={isWaiting}
                                    value={federalWithholdingMode}
                                    onChange={(e) => {
                                        setFederalWithholdingMode(
                                            e.target.value as
                                                | 'percentage'
                                                | 'amount',
                                        );
                                        setFederalWithholdingPct(null);
                                        setFederalWithholdingAmt(null);
                                    }}
                                    style={{
                                        padding: 8,
                                        borderRadius: 8,
                                        borderWidth: 1,
                                        borderColor: '#ccc',
                                        fontSize: 14,
                                        width: 160,
                                    }}
                                >
                                    <option value="percentage">
                                        Percentage
                                    </option>
                                    <option value="amount">
                                        Dollar amount
                                    </option>
                                </select>
                                {federalWithholdingMode === 'percentage' ? (
                                    <InputNumber
                                        id="federalWithholdingPct"
                                        disabled={isWaiting}
                                        value={federalWithholdingPct}
                                        onChange={(e) => {
                                            setFederalWithholdingPct(e.value);
                                        }}
                                        suffix="%"
                                        min={0}
                                        max={100}
                                        placeholder="None"
                                    />
                                ) : (
                                    <InputNumber
                                        id="federalWithholdingAmt"
                                        disabled={isWaiting}
                                        value={federalWithholdingAmt}
                                        onChange={(e) => {
                                            setFederalWithholdingAmt(e.value);
                                        }}
                                        mode="currency"
                                        currency="USD"
                                        min={0}
                                        placeholder="None"
                                    />
                                )}
                            </YStack>
                            <YStack gap={4}>
                                <BodySmallRegular>
                                    State tax withholding (optional)
                                </BodySmallRegular>
                                <select
                                    disabled={
                                        isWaiting || stateWithholdingWaiver
                                    }
                                    value={stateWithholdingMode}
                                    onChange={(e) => {
                                        setStateWithholdingMode(
                                            e.target.value as
                                                | 'percentage'
                                                | 'amount',
                                        );
                                        setStateWithholdingPct(null);
                                        setStateWithholdingAmt(null);
                                    }}
                                    style={{
                                        padding: 8,
                                        borderRadius: 8,
                                        borderWidth: 1,
                                        borderColor: '#ccc',
                                        fontSize: 14,
                                        width: 160,
                                    }}
                                >
                                    <option value="percentage">
                                        Percentage
                                    </option>
                                    <option value="amount">
                                        Dollar amount
                                    </option>
                                </select>
                                {stateWithholdingMode === 'percentage' ? (
                                    <InputNumber
                                        id="stateWithholdingPct"
                                        disabled={
                                            isWaiting || stateWithholdingWaiver
                                        }
                                        value={stateWithholdingPct}
                                        onChange={(e) => {
                                            setStateWithholdingPct(e.value);
                                        }}
                                        suffix="%"
                                        min={0}
                                        max={100}
                                        placeholder="None"
                                    />
                                ) : (
                                    <InputNumber
                                        id="stateWithholdingAmt"
                                        disabled={
                                            isWaiting || stateWithholdingWaiver
                                        }
                                        value={stateWithholdingAmt}
                                        onChange={(e) => {
                                            setStateWithholdingAmt(e.value);
                                        }}
                                        mode="currency"
                                        currency="USD"
                                        min={0}
                                        placeholder="None"
                                    />
                                )}
                            </YStack>
                            <CheckboxWithLabel
                                id="stateWithholdingWaiver"
                                label="Waive state tax withholding"
                                checked={stateWithholdingWaiver}
                                disabled={isWaiting}
                                onPress={() => {
                                    const next = !stateWithholdingWaiver;
                                    setStateWithholdingWaiver(next);
                                    if (next) {
                                        setStateWithholdingPct(null);
                                        setStateWithholdingAmt(null);
                                    }
                                }}
                            />
                        </YStack>
                    )}
                    <SelectAccountSheet
                        isOpen={showAccountSelector}
                        depositAccounts={mappedAccounts}
                        onClose={() => {
                            setShowAccountSelector(false);
                        }}
                        onSelectAccount={(account) => {
                            setSelectedDepositAccount(account);
                        }}
                    />
                    <Button
                        icon={isWaiting ? <Spinner /> : null}
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text={isWaiting ? '' : 'Submit withdrawal'}
                        onPress={async () => {
                            setResult('');
                            await submitWithdrawal(
                                {
                                    body: withdrawlRequest,
                                },
                                {
                                    onSuccess: (body) => {
                                        const msg =
                                            body.stateMessage != null
                                                ? `${body.state}: ${
                                                      body.stateMessage
                                                  }`
                                                : body.state;
                                        setResult(msg);
                                        setHasSubmitted(true);
                                    },
                                    onError: (error) => {
                                        captureException(error);
                                        setResult(error.message);
                                    },
                                },
                            );
                        }}
                        disabled={!canSubmit || isWaiting || hasSubmitted}
                    />
                </YStack>
            </ScrollView>
        </GroveSimpleModal>
    );
};
